import { Select, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import {
  type ExtensionResponse,
  errorMessage,
  getExtensionQueryKey,
  listExtensionConsentQueryKey,
  listExtensionsQueryKey,
  updateExtensionMutation,
} from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";

type Mode = "opt_in" | "opt_out" | "not_allowed";
type Permission = "read" | "write";

/**
 * Who has an integration on (MHS-881): on for everyone until they turn it off (opt-out),
 * or off until each person turns it on (opt-in), separately for reading and for actions.
 */
export function ConsentDefaultsSection({ extension }: Readonly<{ extension: ExtensionResponse }>) {
  const { t } = useTranslation("adminIntegrations");
  const toast = useToast();
  const queryClient = useQueryClient();
  const update = useMutation(updateExtensionMutation());
  if (extension.extensionType !== "integration") return null;

  const change = (permission: Permission, mode: Mode) =>
    update.mutate(
      {
        path: { ext_uuid: extension.uuid },
        body: permission === "read" ? { readConsentMode: mode } : { writeConsentMode: mode },
      },
      {
        onSuccess: async () => {
          await Promise.all(
            [
              getExtensionQueryKey({ path: { ext_uuid: extension.uuid } }),
              listExtensionsQueryKey(),
              listExtensionConsentQueryKey(),
            ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
          );
          toast.success(t("consent.saved"));
        },
        onError: (error) => toast.error(errorMessage(error, t("consent.saveFailed"))),
      },
    );

  return (
    <PageSection title={t("consent.title")} description={t("consent.body")}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ModeSelect
          label={t("consent.read")}
          value={(extension.readConsentMode as Mode) ?? "opt_out"}
          disabled={update.isPending}
          onChange={(mode) => change("read", mode)}
        />
        <ModeSelect
          label={t("consent.write")}
          value={(extension.writeConsentMode as Mode) ?? "opt_in"}
          disabled={update.isPending}
          onChange={(mode) => change("write", mode)}
        />
      </div>
    </PageSection>
  );
}

function ModeSelect({
  label,
  value,
  disabled,
  onChange,
}: Readonly<{ label: string; value: Mode; disabled: boolean; onChange: (mode: Mode) => void }>) {
  const { t } = useTranslation("adminIntegrations");
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <Text as="span" variant="body-sm" aria-hidden>
        {label}
      </Text>
      <Select
        id={id}
        aria-label={label}
        value={value}
        disabled={disabled}
        options={[
          { value: "opt_out", label: t("consent.optOut") },
          { value: "opt_in", label: t("consent.optIn") },
          { value: "not_allowed", label: t("consent.notAllowed") },
        ]}
        onChange={(next) => onChange(next as Mode)}
      />
    </div>
  );
}
